import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ChartFacade,
  ChartId,
  TreatmentStepsFacade,
} from '@principle-theorem/ng-clinical-charting/store';
import {
  ChartedTreatmentUpdater,
  TreatmentStep,
  getAppointmentStepStatus,
} from '@principle-theorem/principle-core';
import {
  type AnyChartedItemConfiguration,
  type IAppointment,
  type IChartedTreatmentSelectionSummary,
  type IChartedMultiStepTreatment,
  type IChartedMultiStepTreatmentStep,
  type IChartedRef,
  type IChartedTreatment,
  type IFeeSchedule,
  type ITreatmentCategory,
  type ITreatmentPlan,
  type ITreatmentStep,
  type PlanStepPairStatus,
  isChartedMultiStepTreatment,
  isChartedMultiStepTreatmentStep,
  isTreatmentPlan,
} from '@principle-theorem/principle-core/interfaces';
import {
  doc$,
  getDoc,
  isWithRef,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { ChartDialogService } from '../../chart-dialog.service';
import { AddChartable } from '../../add-chartable';
import { type IChartedSurfaceProvider } from '../../charted-surface/add-charted-surface-provider';
import { AddTreatmentToMultiTreatmentProposalProvider } from '../../charted-surface/chart/add-treatment-to-multi-treatment-proposal-provider';
import { AddMultiTreatmentToFirestorePlanProvider } from '../../charted-surface/firestore-treatment-plan/add-multi-treatment-to-firestore-plan-provider';
import { AddTreatmentToStoreStepProvider } from '../../charted-surface/store-treatment-plan/add-treatment-to-store-step-provider';
import { AddMultiTreatmentToMultiTreatmentProposalProvider } from '../../charted-surface/chart/add-multi-treatment-to-multi-treatment-proposal-provider';
import {
  ReplaySubject,
  Subject,
  combineLatest,
  of,
  type Observable,
} from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { type IEditChartableData } from '../../chartable-surface-updater';
import { TreatmentStepsDisplayService } from '../treatment-steps-editor/treatment-steps-display.service';
import { SelectionListStore } from '@principle-theorem/ng-shared';

@Component({
  selector: 'pr-treatment-step',
  templateUrl: './treatment-step.component.html',
  styleUrls: ['./treatment-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TreatmentStepComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private readonly _selectionList = inject(
    SelectionListStore<IChartedTreatmentSelectionSummary>,
    { optional: true }
  );
  step$ = new ReplaySubject<
    WithRef<ITreatmentStep> | IChartedMultiStepTreatmentStep
  >(1);
  plan$ = new ReplaySubject<
    IChartedMultiStepTreatment | WithRef<ITreatmentPlan>
  >(1);
  treatments$: Observable<IChartedTreatment[]>;
  appointment$: Observable<WithRef<IAppointment> | undefined>;
  status$: Observable<PlanStepPairStatus>;
  @Input() inlcudeMultiTreatments = true;
  @Input() hideDelete = false;
  @Input() hideDepositNotification = false;
  @Input() filterByStatus = true;
  @Output()
  stepChanged = new EventEmitter<
    Partial<IChartedMultiStepTreatmentStep | WithRef<ITreatmentStep>>
  >();
  @Output()
  stepDeleted = new EventEmitter<ITreatmentStep>();
  @Output() moveTreatmentsHere = new EventEmitter<void>();
  isComplete$: Observable<boolean>;
  isIncomplete$: Observable<boolean>;
  hasSelection$: Observable<boolean>;
  @Output() updateChartable = new EventEmitter<IEditChartableData>();
  expanded = false;
  displayStep = signal(true);
  status = signal<PlanStepPairStatus | undefined>(undefined);

  @Input()
  set step(step: WithRef<ITreatmentStep> | IChartedMultiStepTreatmentStep) {
    if (step) {
      this.step$.next(step);
    }
  }

  @Input()
  set plan(plan: IChartedMultiStepTreatment | WithRef<ITreatmentPlan>) {
    if (plan) {
      this.plan$.next(plan);
    }
  }

  constructor(
    public elementRef: ElementRef,
    private _snackBar: MatSnackBar,
    private _treatmentStepsDisplay: TreatmentStepsDisplayService,
    private _chartFacade: ChartFacade,
    private _treatmentStepsFacade: TreatmentStepsFacade,
    private _chartDialog: ChartDialogService
  ) {
    this.isComplete$ = this.step$.pipe(
      map((step) => TreatmentStep.isComplete(step))
    );

    this.isIncomplete$ = this.step$.pipe(
      map((step) => TreatmentStep.isIncomplete(step))
    );

    this.hasSelection$ =
      this._selectionList?.selected$.pipe(
        map((selection) => selection.length > 0)
      ) ?? of(false);

    this.treatments$ = this.step$.pipe(map((step) => step.treatments));

    this.appointment$ = this.step$.pipe(
      switchMap((step) =>
        step.appointment ? doc$(step.appointment) : of(undefined)
      )
    );

    this.status$ = combineLatest([this.step$, this.appointment$]).pipe(
      map(([step, appointment]) => getAppointmentStepStatus(step, appointment))
    );

    this.status$.pipe(takeUntil(this._onDestroy$)).subscribe((status) => {
      this.status.set(status);
      if (!this.filterByStatus) {
        return;
      }
      const activeFilters = this._treatmentStepsDisplay.activeStatusFilters();
      const shouldDisplay = !!activeFilters && activeFilters.includes(status);
      this.displayStep.set(shouldDisplay);
    });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async updateTreatments(treatments: IChartedTreatment[]): Promise<void> {
    this.stepChanged.emit({
      treatments: await ChartedTreatmentUpdater.syncPricingRules(treatments),
    });
  }

  async deleteTreatment(deletedTreatments: IChartedTreatment[]): Promise<void> {
    const step = await snapshot(this.step$);
    const remainingTreatments = TreatmentStep.deleteTreatments(
      step.treatments,
      deletedTreatments
    );
    const treatments =
      await ChartedTreatmentUpdater.syncPricingRules(remainingTreatments);

    this.stepChanged.emit({ treatments });
    this._snackBar.open(
      TreatmentStep.deletedTreatmentsFeedback(deletedTreatments.length)
    );
  }

  async updateStepDisplayOverride(
    selectedOverride: WithRef<ITreatmentCategory>
  ): Promise<void> {
    const treatmentStep = await snapshot(this.step$);
    const display = TreatmentStep.updateDisplayOverrideCategory(
      treatmentStep,
      selectedOverride.ref
    );

    if (!display) {
      return;
    }

    this.stepChanged.emit({ display });
  }

  async addChartable(
    chartable: AnyChartedItemConfiguration,
    selectedSurfaces: Partial<IChartedRef>[]
  ): Promise<void> {
    const addSurfaceProviders = await this._buildServiceProviders();
    await new AddChartable(addSurfaceProviders).add({
      selectedSurfaces,
      chartable,
      chartingAs: await snapshot(
        this._chartFacade.chartingAs$(ChartId.InAppointment)
      ),
    });
    this._chartFacade.setSelectedSurfaces(ChartId.InAppointment, []);
  }

  private async _buildServiceProviders(): Promise<IChartedSurfaceProvider[]> {
    const step = await snapshot(this.step$);
    const plan = await snapshot(this.plan$);
    const feeSchedule = await this._resolveFeeSchedule(plan);
    const addSurfaceProviders: IChartedSurfaceProvider[] = [];

    if (isWithRef<ITreatmentStep>(step)) {
      addSurfaceProviders.push(
        new AddTreatmentToStoreStepProvider(
          this._treatmentStepsFacade,
          step,
          feeSchedule
        )
      );
      if (isWithRef(plan)) {
        addSurfaceProviders.push(
          new AddMultiTreatmentToFirestorePlanProvider(
            plan,
            step,
            this._chartDialog
          )
        );
      }
    }

    if (
      isChartedMultiStepTreatment(plan) &&
      isChartedMultiStepTreatmentStep(step)
    ) {
      addSurfaceProviders.push(
        new AddTreatmentToMultiTreatmentProposalProvider(
          this._chartFacade,
          plan,
          step,
          feeSchedule
        ),
        new AddMultiTreatmentToMultiTreatmentProposalProvider(
          this._chartFacade,
          plan,
          step,
          this._chartDialog
        )
      );
    }
    return addSurfaceProviders;
  }

  private async _resolveFeeSchedule(
    plan: ITreatmentPlan | IChartedMultiStepTreatment
  ): Promise<WithRef<IFeeSchedule>> {
    if (isTreatmentPlan(plan) && plan.feeSchedule) {
      return getDoc(plan.feeSchedule.ref);
    }
    return snapshot(this._chartFacade.currentSchedule$);
  }
}
